import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction step 1 (and the By cost code view) — "Bid packages", the
// prototype's Edit packages pop-up. It works on a draft on the page: move a
// cost code into another package or into its own, Ungroup, rename, Use AI's
// packages, One package per cost code. Save packages sends the whole list
// (the server keeps each package's record, so its bidders and bids stay);
// Cancel, ×, Escape or the backdrop throw the draft away. Opens when
// precon#openEditPackages un-hides it.
export default class extends Controller {
  static targets = ["modal"]
  connect() {
    let j = {}
    try { j = JSON.parse(this.element.querySelector("script[data-bp-json]")?.textContent || "{}") } catch (e) { j = {} }
    this.dataValue = j.data || { packages: [], codes: {}, order: [] }
    this.stringsValue = j.strings || {}
    this._observer = new MutationObserver(() => {
      const open = !this.element.classList.contains("hidden")
      if (open && !this._open) this._start()
      this._open = open
    })
    this._observer.observe(this.element, { attributes: true, attributeFilter: ["class"] })
  }

  disconnect() { this._observer?.disconnect() }

  get page() {
    const el = this.element.closest("[data-controller~='precon']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "precon") : null
  }

  _t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), this.stringsValue)
    if (s && typeof s === "object") s = vars.count === 1 ? s.one : s.other
    if (typeof s !== "string") return key
    return s.replace(/%\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : ""))
  }

  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }
  _money(n) { return n === null || n === undefined ? "—" : "$" + Math.round(Number(n)).toLocaleString("en-US") }

  // The draft: the groups (cards). A cost code in no group is a package on its own.
  _start() {
    const d = this.dataValue
    this._draft = d.packages.filter(p => !p.one).map(p => ({ id: p.id, name: p.name, codes: p.codes.slice(), ai: !!p.ai, why: p.why || "", named: true }))
    this._singles = {}
    d.packages.filter(p => p.one).forEach(p => { if (p.codes[0]) this._singles[p.codes[0]] = p.id })
    this._moveFor = null
    this._seq = 0
    this._draw()
  }

  // The prototype's bpList: groups in the order of their first cost code,
  // then a package for each cost code left; numbers follow that order.
  _list() {
    const d = this.dataValue
    const rank = {}
    ;(d.order || []).forEach((dv, i) => { rank[dv] = i })
    const seq = Object.keys(d.codes).map(Number).sort((a, b) => {
      const A = d.codes[a], B = d.codes[b]
      return (rank[A.div] ?? 999) - (rank[B.div] ?? 999) || (A.code < B.code ? -1 : A.code > B.code ? 1 : 0)
    })
    const pos = {}
    seq.forEach((id, i) => { pos[id] = i })
    const used = {}
    const out = []
    this._draft.forEach(g => {
      const ps = g.codes.filter(id => pos[id] !== undefined && !used[id])
      if (!ps.length) return
      ps.forEach(id => { used[id] = true })
      ps.sort((a, b) => pos[a] - pos[b])
      out.push({ g, group: true, ps, name: g.name || d.codes[ps[0]].name })
    })
    seq.forEach(id => { if (!used[id]) out.push({ g: null, group: false, ps: [id], name: d.codes[id].name }) })
    out.sort((a, b) => pos[a.ps[0]] - pos[b.ps[0]])
    out.forEach((e, i) => { e.n = i + 1 })
    return out
  }

  _move(id, target) {
    let dr = this._draft.map(g => (g.codes.includes(id) ? Object.assign({}, g, { codes: g.codes.filter(c => c !== id), ai: false }) : g))
    if (target && target.group) {
      dr = dr.map(g => (g === target.g ? Object.assign({}, g, { codes: g.codes.concat([id]), ai: false }) : g))
    } else if (target) {
      dr.push({ id: null, key: `n${++this._seq}`, name: this.dataValue.codes[target.ps[0]].name, codes: [target.ps[0], id], ai: false, why: "", named: false })
    } else {
      dr.push({ id: null, key: `o${++this._seq}`, name: this.dataValue.codes[id].name, codes: [id], ai: false, why: "", named: true })
    }
    this._draft = dr.filter(g => g.codes.length > 1 || (g.codes.length === 1 && g.named))
    this._moveFor = null
    this._draw()
  }

  _draw() {
    const d = this.dataValue
    const list = this._list()
    const grouped = list.filter(e => e.group && e.ps.length > 1).length
    const amt = (ps) => ps.reduce((a, id) => a + (Number(d.codes[id].amt) || 0), 0)
    const isAi = (ps) => ps.every(id => d.codes[id].ai)
    const divsOf = (ps) => Array.from(new Set(ps.map(id => d.codes[id].div)))
    const sparkles = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"></path></svg>`
    const chev = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m19.5 8.25-7.5 7.5-7.5-7.5"></path></svg>`
    const x = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"></path></svg>`
    const moveMenu = (id, entry) => {
      const key = `${id}`
      const open = this._moveFor === key
      const opts = list.filter(e => e !== entry).map((e, i) => `<button class="menuitem" type="button" role="menuitem" data-to="${list.indexOf(e)}" data-code="${id}">${this._esc(this._t("bp.into", { n: e.n, name: e.name }))}</button>`)
      if (entry.group && entry.ps.length > 1) opts.unshift(`<button class="menuitem" type="button" role="menuitem" data-own="${id}">${this._esc(this._t("bp.its_own"))}</button>`)
      const c = d.codes[id]
      return `<span class="bpmv"><button class="linkbtn" type="button" data-toggle="${key}" aria-expanded="${open}" aria-label="${this._esc(this._t("bp.move_aria", { code: `${c.code} ${c.name}` }))}">${this._esc(this._t("bp.move"))}${chev}</button>${open ? `<div class="menuback" data-closemenu></div><div class="menu bpmenu" role="menu">${opts.join("")}</div>` : ""}</span>`
    }
    const body = list.map((e, idx) => {
      if (!e.group) {
        const id = e.ps[0]; const c = d.codes[id]
        return `<div class="bpone"><span class="bpnum">${e.n}</span><span class="code">${this._esc(c.code)}</span><span class="grow trunc">${this._esc(c.name)}</span><span class="bpamt ${c.ai ? "aiv" : ""}">${this._money(c.amt)}</span>${moveMenu(id, e)}</div>`
      }
      const dvs = divsOf(e.ps)
      const codes = this._t("n_codes", { count: e.ps.length })
      const meta = dvs.length > 1 ? this._t("bp.meta_multi", { codes, count: dvs.length, amount: this._money(amt(e.ps)) }) : this._t("bp.meta", { codes, amount: this._money(amt(e.ps)) })
      return `<div class="bpcard"><div class="bpch"><span class="bpnum">${e.n}</span><input class="inp bpname" type="text" value="${this._esc(e.g.name)}" data-name="${idx}" aria-label="${this._esc(this._t("bp.name_aria", { n: e.n }))}"><span class="muted">${this._esc(meta)}</span>${e.ps.length > 1 ? `<button class="linkbtn" type="button" data-split="${idx}">${this._esc(this._t("bp.ungroup"))}</button>` : ""}</div>` +
        (e.g.ai && e.g.why ? `<div class="bpwhy">${sparkles}<span>${this._esc(e.g.why)}</span></div>` : "") +
        e.ps.map(id => { const c = d.codes[id]; return `<div class="bpcode"><span class="code">${this._esc(c.code)}</span><span class="grow trunc">${this._esc(c.name)}</span><span class="bpamt ${c.ai ? "aiv" : ""}">${this._money(c.amt)}</span>${moveMenu(id, e)}</div>` }).join("") + `</div>`
    }).join("")
    this.modalTarget.innerHTML = `
      <div class="mhead"><div><h2 class="cardtitle">${this._esc(this._t("bp.title"))}</h2><div class="cardsub">${this._esc(this._t("bp.sub"))}</div></div><button class="x" type="button" data-cancel aria-label="${this._esc(this._t("close"))}">${x}</button></div>
      <div class="pad col12">
        <div class="row8 bptools"><button class="btn btn-secondary btn-sm" type="button" data-ai>${sparkles}<span>${this._esc(this._t("bp.use_ai"))}</span></button><button class="btn btn-secondary btn-sm" type="button" data-reset>${this._esc(this._t("bp.one_each"))}</button><span class="muted">${this._esc(this._t("bp.count", { count: list.length, grouped }))}</span></div>
        <div class="bplist">${body}</div>
      </div>
      <div class="mfoot"><button class="btn btn-secondary" type="button" data-cancel>${this._esc(this._t("cancel"))}</button><button class="btn btn-primary" type="button" data-save>${this._esc(this._t("bp.save"))}</button></div>`
    this._entries = list
    if (!this._wired) this._wire()
  }

  _wire() {
    this._wired = true
    const m = this.modalTarget
    m.addEventListener("click", (e) => {
      e.stopPropagation()
      const t = e.target
      if (t.closest("[data-cancel]")) return this._close()
      if (t.closest("[data-save]")) return this._save()
      if (t.closest("[data-closemenu]")) { this._moveFor = null; return this._draw() }
      const tog = t.closest("[data-toggle]")
      if (tog) { this._moveFor = this._moveFor === tog.dataset.toggle ? null : tog.dataset.toggle; return this._draw() }
      const own = t.closest("[data-own]")
      if (own) return this._move(Number(own.dataset.own), null)
      const to = t.closest("[data-to]")
      if (to) return this._move(Number(to.dataset.code), this._entries[Number(to.dataset.to)])
      const split = t.closest("[data-split]")
      if (split) {
        const e2 = this._entries[Number(split.dataset.split)]
        this._draft = this._draft.filter(g => g !== e2.g).concat(e2.ps.map(id => ({ id: null, key: `s${++this._seq}`, name: this.dataValue.codes[id].name, codes: [id], ai: false, why: "", named: true })))
        return this._draw()
      }
      if (t.closest("[data-ai]")) return this._useAi()
      if (t.closest("[data-reset]")) { this._draft = []; return this._draw() }
    })
    m.addEventListener("change", (e) => {
      const i = e.target.dataset.name
      if (i === undefined) return
      const g = this._entries[Number(i)]?.g
      if (g) { g.name = e.target.value; g.named = true; g.ai = false }
    })
  }

  backdrop(e) { if (e.target === this.element) this._close() }

  _close() {
    this.element.classList.add("hidden")
    this._open = false
  }

  // AI's packages: the plan AI made (kept on the job); with none kept, AI
  // sorts them again (a few seconds, the banner shows while it works).
  _useAi() {
    const ai = this.dataValue.ai
    if (ai && ai.length) {
      this._draft = ai.map((g, i) => ({ id: null, key: `a${i}`, name: g.name, codes: (g.cost_code_ids || []).map(Number), ai: !!g.ai, why: g.why || "", named: true }))
        .filter(g => g.codes.length > 1 || g.ai)
      return this._draw()
    }
    const page = this.page
    this._close()
    track("Precon AI Packages Requested", { project_id: page?.projectIdValue })
    if (!page) return
    const q = page._viewQuery
    page._json("POST", `${page.baseUrlValue}/packages/use_ai${q ? "?" + q : ""}`, {})
      .then(data => { page._applyResponse(data); page._showToast(this._t("bp.ai_running")) })
      .catch(err => page._showToast(err.message, "error"))
  }

  async _save() {
    const page = this.page
    if (!page) return
    const list = this._list()
    const payload = list.map(e => {
      if (e.group) return { id: e.g.id || "", name: (e.g.name || "").trim() || this.dataValue.codes[e.ps[0]].name || this._t("bp.default_name"), cost_code_ids: e.ps, ai: !!e.g.ai, why: e.g.why || "" }
      return { id: this._singles[e.ps[0]] || "", name: this.dataValue.codes[e.ps[0]].name, cost_code_ids: e.ps, ai: false, why: "" }
    })
    const grouped = list.filter(e => e.group && e.ps.length > 1).length
    this._close()
    track("Precon Packages Saved", { project_id: page.projectIdValue, packages: list.length, grouped })
    try {
      const q = page._viewQuery
      const data = await page._json("POST", `${page.baseUrlValue}/packages/save${q ? "?" + q : ""}`, { packages: payload })
      page._applyResponse(data)
      page._showToast(this._t("bp.saved", { packages: this._t("n_packages", { count: data.packages ?? list.length }), grouped: data.grouped ?? grouped }))
    } catch (err) {
      page._showToast(err.message, "error")
    }
  }
}
